/**
 * The slope direction of a roof face, read off its footprint.
 *
 * A roof face falls away from its ridge towards its eave, so its azimuth is
 * the outward normal of the eave edge - which on almost every face is the
 * longest edge. These helpers give the plan editor that reading: the outward
 * azimuth of any edge, the longest edge's, and the bearing of a dragged
 * arrow, all in the platform's compass convention (0 = north, 90 = east,
 * 180 = south) on plan coordinates where x is east and y is north.
 */

/** Compass bearing of a plan direction, 0..360. */
export function bearingOf(dx, dy) {
  if (!Number.isFinite(dx) || !Number.isFinite(dy) || (dx === 0 && dy === 0)) return null;
  return ((Math.atan2(dx, dy) * 180) / Math.PI + 360) % 360;
}

/** The area centroid of a ring; the vertex mean when the ring is degenerate. */
export function polygonCentroid(ring) {
  const pts = (ring || []).filter((p) => Number.isFinite(p?.x) && Number.isFinite(p?.y));
  if (!pts.length) return null;
  let twice = 0, cx = 0, cy = 0;
  for (let i = 0; i < pts.length; i += 1) {
    const a = pts[i], b = pts[(i + 1) % pts.length];
    const cross = a.x * b.y - b.x * a.y;
    twice += cross; cx += (a.x + b.x) * cross; cy += (a.y + b.y) * cross;
  }
  if (Math.abs(twice) < 1e-9) {
    return { x: pts.reduce((s, p) => s + p.x, 0) / pts.length, y: pts.reduce((s, p) => s + p.y, 0) / pts.length };
  }
  return { x: cx / (3 * twice), y: cy / (3 * twice) };
}

/** The azimuth a face would have if edge i (from vertex i to i+1) were its eave. */
export function edgeOutwardAzimuth(ring, i) {
  const centre = polygonCentroid(ring);
  if (!centre || ring.length < 2) return null;
  const a = ring[i], b = ring[(i + 1) % ring.length];
  const ex = b.x - a.x, ey = b.y - a.y;
  if (Math.hypot(ex, ey) < 1e-9) return null;
  // Either perpendicular; the one pointing away from the centre is outward.
  let nx = ey, ny = -ex;
  const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
  if (nx * (mid.x - centre.x) + ny * (mid.y - centre.y) < 0) { nx = -nx; ny = -ny; }
  return bearingOf(nx, ny);
}

/**
 * The longest edge's outward azimuth: the eave, on almost every roof face.
 * A rectangle has two longest edges; of edges within a hand's width of the
 * longest, the one nearest the face's current azimuth wins, so a south-facing
 * rectangle stays south rather than flipping to its ridge side.
 */
export function longestEdgeAzimuth(ring, currentAzimuth = null) {
  if (!Array.isArray(ring) || ring.length < 3) return null;
  const lengths = ring.map((a, i) => { const b = ring[(i + 1) % ring.length]; return Math.hypot(b.x - a.x, b.y - a.y); });
  const longest = Math.max(...lengths);
  if (!(longest > 0)) return null;
  const candidates = lengths.map((l, i) => (l >= longest - 0.05 ? edgeOutwardAzimuth(ring, i) : null)).filter((a) => a != null);
  if (!candidates.length) return null;
  if (!Number.isFinite(currentAzimuth)) return candidates[0];
  const apart = (a) => Math.abs(((a - currentAzimuth + 540) % 360) - 180);
  return candidates.reduce((best, a) => (apart(a) < apart(best) ? a : best), candidates[0]);
}

/** Round a dragged bearing to whole degrees, 0..359. */
export function roundAzimuth(deg) {
  if (!Number.isFinite(deg)) return null;
  return ((Math.round(deg) % 360) + 360) % 360;
}
